/**
 * Hide and Seek Teams — one coin toss, in the round list just above its first
 * round: what it decides, its rounds, the coin, and the result. The admin
 * flips it (again), changes or removes it until one of its rounds is loaded
 * or played, and can make the winning Team Captain's choice for them while
 * their time runs.
 */

import { useState } from 'react';
import {
  HNST_COIN_TOSS_KIND_LABELS,
  HNST_COIN_TOSS_MODE_LABELS,
  HNST_TEAMS,
  getPlayerDisplayName,
  hnstCoinTossLabel,
  hnstTossFirstHidingTeam,
  hnstTossLockedReason,
  hnstTossRoundIndexes,
  isHnstSettings,
  type Game,
  type HnstActionResponse,
  type HnstCoinChoice,
  type HnstCoinToss,
} from '@swtorbit/shared-socket-events';
import { HnstCoin, HnstTeamName, useHnstCoinChoiceTime, useHnstCoinPhase } from '@swtorbit/shared-components';
import { useServerClockOffset } from '@swtorbit/shared-react-hooks';
import { Progress } from '@swtorbit/ui/react';
import { useHnstContext } from '../../contexts/HnstContext';

const DECIDED_BY: Record<NonNullable<HnstCoinToss['decidedBy']>, string> = {
  auto: '',
  captain: ' (their Team Captain chose)',
  admin: ' (you chose for their Team Captain)',
  timeout: ' (their Team Captain ran out of time: chosen at random)',
};

/** The toss's rounds as a short list: "Rounds 1, 2 and 4", or each team's pick for a picks toss. */
function roundsText(game: Game, toss: HnstCoinToss): string {
  const indexes = hnstTossRoundIndexes(game, toss.id);
  if (indexes.length === 0) return 'No rounds yet';
  if (toss.kind === 'picks') {
    return indexes
      .map((i) => {
        const s = game.rounds[i].settings;
        return isHnstSettings(s) && s.ownerTeam ? `${HNST_TEAMS[s.ownerTeam].name}' pick (round ${i + 1})` : `Round ${i + 1}`;
      })
      .join(' and ');
  }
  const numbers = indexes.map((i) => `${i + 1}`);
  const list = numbers.length > 1 ? `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}` : numbers[0];
  return `Round${numbers.length > 1 ? 's' : ''} ${list}`;
}

export function HnstCoinTossCard({ game, toss, onEdit }: { game: Game; toss: HnstCoinToss; onEdit: (toss: HnstCoinToss) => void }) {
  const actions = useHnstContext();
  const [error, setError] = useState<string | null>(null);
  const serverOffsetMs = useServerClockOffset();
  const landed = useHnstCoinPhase(toss.flip, serverOffsetMs) === 'landed';
  const choosing = useHnstCoinChoiceTime(toss, serverOffsetMs);
  const locked = hnstTossLockedReason(game, toss.id);
  const label = hnstCoinTossLabel(game, toss.id);
  const winner = toss.flip?.winner ?? null;
  const first = hnstTossFirstHidingTeam(toss);

  const run = async (action: Promise<HnstActionResponse>) => {
    const res = await action;
    setError(res.success ? null : res.error ?? 'Something went wrong');
  };
  const remove = () => {
    if (window.confirm(`Remove ${label}? Its rounds go back to the turn order.`)) void run(actions.deleteCoinToss(toss.id));
  };
  const choiceLabels: Record<HnstCoinChoice, string> =
    toss.kind === 'picks' ? { hide: 'Their pick first', seek: 'The other pick first' } : { hide: 'Hide first', seek: 'Seek first' };

  return (
    <div className="card card--sm card--bg-midnight card--border card--border-amber mb-sm">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-xs">
        <h3 className="mb-0">🪙 {label} — {HNST_COIN_TOSS_KIND_LABELS[toss.kind]}</h3>
        <span className="badge badge--soft badge--amber">{HNST_COIN_TOSS_MODE_LABELS[toss.mode]}</span>
      </div>
      <p className="fs-sm text-slate mb-xs">
        {roundsText(game, toss)}.{' '}
        {toss.kind === 'picks'
          ? 'The toss decides whose pick is played first; each team hides in its own pick.'
          : 'The toss decides who hides in the first of them; the teams then take turns.'}
      </p>
      {error && <div className="alert alert--red">{error}</div>}

      <div className="d-flex align-items-center gap-base">
        {toss.flip && <HnstCoin flip={toss.flip} size="sm" serverOffsetMs={serverOffsetMs} />}
        <div className="d-flex flex-column gap-2xs">
          {!toss.flip && <p className="mb-0">Not flipped yet.</p>}
          {toss.flip && !landed && <p className="fs-lg mb-0">The coin is in the air…</p>}
          {winner && landed && <p className="fs-lg mb-0"><HnstTeamName team={winner} /> win the toss!</p>}
          {winner && landed && first && toss.decidedBy && (
            <p className="mb-0">
              {toss.kind === 'picks'
                ? <><HnstTeamName team={first} />&apos; pick plays first</>
                : <><HnstTeamName team={first} /> hide first, then the teams take turns</>}
              {DECIDED_BY[toss.decidedBy]}.
            </p>
          )}
          {winner && choosing && (
            <>
              <p className="mb-0">
                {getPlayerDisplayName(game.hnstTeams?.find((t) => t.key === winner)?.captainPlayerId ?? '', game.players)}, their
                Team Captain, is choosing… {choosing.secondsLeft}s
              </p>
              <Progress value={choosing.percentLeft} hue="amber" size="sm" />
              {!locked && (
                <div className="d-flex flex-wrap gap-xs">
                  {(['hide', 'seek'] as const).map((choice) => (
                    <button key={choice} className="btn btn--outline btn--slate" onClick={() => run(actions.chooseForCaptain(toss.id, choice))}>
                      {choiceLabels[choice]} for them
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {locked ? (
        <p className="field__hint fs-xs mt-xs mb-0">Fixed now: {locked.charAt(0).toLowerCase()}{locked.slice(1)}.</p>
      ) : (
        <div className="d-flex flex-wrap gap-xs mt-xs">
          <button className="btn btn--amber" onClick={() => run(actions.flipCoin(toss.id))}>
            🪙 {toss.flip ? 'Flip Again' : 'Flip Coin'}
          </button>
          <button className="btn btn--aqua" onClick={() => onEdit(toss)}>✏️ Edit</button>
          <button className="btn btn--red" onClick={remove}>🗑️ Remove</button>
        </div>
      )}
    </div>
  );
}
